import { booksChanged } from '../lib/changed';
import { supabase } from '../lib/supabase';
import { candidatesOf, differencesOf, fillRows, needsOf, outcomeOf } from '../../supabase/functions/_shared/nightly/nightly.ts';
import type { LookupAnswer } from '../../supabase/functions/_shared/lookup/types.ts';
import type { Candidate, Difference, Dismissal, LookupRow, NowFacts } from './lookups';

// Review's lookup chips (step 2): reads the nightly fill-in's record (work_lookups, admin-read for
// now) and your answers (review_dismissals), and saves answers: a pick fills right away through
// book-lookup and review_apply_fills (0044); fixes go through book_edit and copy_edit.

type Row = Record<string, unknown>;

function db() {
  if (!supabase) throw new Error('Not connected to a database.');
  return supabase;
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** What the nightly fill-in left for Review: books it couldn't settle or found differences on. */
export async function loadLookups(): Promise<LookupRow[]> {
  const all: Row[] = [];
  for (let from = 0; from < 50000; from += 1000) {
    const { data, error } = await db()
      .from('work_lookups')
      .select('work_id, result, needs_you, candidates, differences, looked_up_at')
      .order('work_id')
      .range(from, from + 999);
    if (error) throw new Error(error.message);
    const page = (data as Row[] | null) ?? [];
    all.push(...page);
    if (page.length < 1000) break;
  }
  const rows = all
    .map((r) => ({
      workId: r.work_id as string,
      result: (r.result as string) ?? '',
      needs: list<string>(r.needs_you),
      candidates: list<Candidate>(r.candidates),
      differences: list<Difference>(r.differences),
      lookedUpAt: (r.looked_up_at as string) ?? '',
    }))
    .filter((r) => r.result === 'choose' || r.result === 'not_found' || r.needs.length || r.differences.length);
  const now = await loadNow(rows.filter((r) => r.needs.length || r.differences.length).map((r) => r.workId));
  return rows.map((r) => (now.has(r.workId) ? { ...r, now: now.get(r.workId) } : r));
}

/** The catalog as it is now for these books (shared rows only, as the lookups see them). */
async function loadNow(workIds: string[]): Promise<Map<string, NowFacts>> {
  const out = new Map<string, NowFacts>();
  for (let i = 0; i < workIds.length; i += 150) {
    const { data, error } = await db()
      .from('works')
      .select('id, summary, first_published_on, fiction, editions(id, kind, asin, audible_id, pages, cover_url, merged_into, private_to)')
      .in('id', workIds.slice(i, i + 150));
    if (error) throw new Error(error.message);
    for (const w of (data as Row[] | null) ?? []) {
      const eds = list<Row>(w.editions).filter((e) => !e.merged_into && !e.private_to);
      out.set(w.id as string, {
        summary: Boolean(w.summary),
        firstYear: typeof w.first_published_on === 'string' ? Number(w.first_published_on.slice(0, 4)) : null,
        fiction: (w.fiction as string | null) ?? null,
        cover: eds.some((e) => e.cover_url),
        editions: eds.map((e) => ({
          id: e.id as string,
          kind: e.kind as string,
          asin: (e.asin as string | null) ?? null,
          audibleId: (e.audible_id as string | null) ?? null,
          pages: (e.pages as number | null) ?? null,
        })),
      });
    }
  }
  return out;
}

export async function loadDismissals(): Promise<Dismissal[]> {
  const { data, error } = await db().from('review_dismissals').select('id, book_id, field, kind, found_value');
  if (error) throw new Error(error.message);
  return ((data as Row[] | null) ?? []).map((r) => ({
    id: r.id as string,
    bookId: r.book_id as string,
    field: r.field as string,
    kind: r.kind as Dismissal['kind'],
    foundValue: (r.found_value as string | null) ?? null,
  }));
}

/** Remembers an answer (one per book and field); returns how to take it back. */
export async function dismiss(bookId: string, field: string, kind: Dismissal['kind'], foundValue: string | null): Promise<() => Promise<void>> {
  const { data: before } = await db().from('review_dismissals').select('kind, found_value').eq('book_id', bookId).eq('field', field).maybeSingle();
  const { error } = await db()
    .from('review_dismissals')
    .upsert({ book_id: bookId, field, kind, found_value: foundValue, updated_at: new Date().toISOString() }, { onConflict: 'book_id,field' });
  if (error) throw new Error(error.message);
  return async () => {
    const q = db().from('review_dismissals');
    const { error: e } = before
      ? await q.update({ kind: (before as Row).kind, found_value: (before as Row).found_value }).eq('book_id', bookId).eq('field', field)
      : await q.delete().eq('book_id', bookId).eq('field', field);
    if (e) throw new Error(e.message);
  };
}

export interface PickResult {
  run: string;
  filled: number;
  /** A site didn't answer: what's still blank fills that night. */
  tonight: boolean;
}

/** Fills the book from the record you picked, right away (Kristi, Oct 30). */
export async function pickMatch(bookId: string, workId: string, pick: { openLibraryId?: string | null; googleBooksId?: string | null }): Promise<PickResult> {
  const body = pick.openLibraryId ? { workId, openLibraryId: pick.openLibraryId } : { workId, googleBooksId: pick.googleBooksId ?? undefined };
  const { data, error } = await db().functions.invoke('book-lookup', { body });
  const answer = error ? null : (data as LookupAnswer);
  const out = answer ? outcomeOf(answer) : 'error';
  // Only a match is saved as filled; anything else waits for that night.
  const outcome = out === 'filled' || out === 'nothing_new' ? out : 'error';
  const res = await rpc<{ run: string; fields_filled: number }>('review_apply_fills', {
    book: bookId,
    fills: answer && outcome !== 'error' ? fillRows(answer) : [],
    outcome,
    needs: answer ? needsOf(answer) : [],
    problems: answer ? answer.errors : ['book-lookup didn’t answer.'],
    ol_calls: answer?.calls.openLibrary ?? 0,
    gb_calls: answer?.calls.google ?? 0,
    cands: answer ? candidatesOf(answer) : [],
    diffs: answer ? differencesOf(answer) : [],
  });
  booksChanged();
  return { run: res.run, filled: res.fields_filled ?? 0, tonight: outcome === 'error' || Boolean(answer?.errors.length) };
}

export async function undoPick(run: string): Promise<void> {
  await rpc('review_undo_fills', { run });
  booksChanged();
}

/** One change through the book's usual functions; returns its action, for Undo. */
export async function edit(fn: 'book_edit' | 'copy_edit' | 'book_remove', args: Record<string, unknown>): Promise<string | null> {
  const a = await rpc<string | null>(fn, args);
  booksChanged();
  return a;
}

export const undoEdit = (action: string) => rpc('undo_action', { action }).then(() => booksChanged());

/** Your copy for a store ID (Kindle for an ASIN, Audible for an Audible ID), or one on a given edition. */
export async function copyFor(bookId: string, want: { format?: 'kindle' | 'audible'; editionId?: string; withIsbn?: boolean }): Promise<string | null> {
  let q = db().from('copies').select('id, format, edition_id, how, edition:editions(isbn13, kind)').eq('book_id', bookId);
  if (want.editionId) q = q.eq('edition_id', want.editionId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const rows = (data as Row[] | null) ?? [];
  const ed = (r: Row) => (Array.isArray(r.edition) ? r.edition[0] : r.edition) as Row | null;
  // A Kindle or Audible ID belongs on the ebook or audiobook edition: your own copy in that store
  // first, else any copy of that kind of edition (a Libby audiobook loan is the same recording;
  // found on The Redbreast, Oct 30).
  const kind = want.format === 'audible' ? 'audiobook' : 'ebook';
  const pick = want.format
    ? rows.find((r) => r.format === want.format && r.how !== 'library_loan') ??
      rows.find((r) => r.format === want.format) ??
      rows.find((r) => ed(r)?.kind === kind)
    : want.withIsbn
      ? rows.find((r) => ed(r)?.isbn13) ?? rows[0]
      : rows[0];
  return (pick?.id as string | undefined) ?? null;
}

/** The edition a found cover goes on: the nightly fill-in's rule (a non-audiobook edition first). */
export async function coverEdition(workId: string): Promise<string | null> {
  const { data, error } = await db().from('editions').select('id, kind').eq('work_id', workId).is('merged_into', null).is('private_to', null).order('id');
  if (error) throw new Error(error.message);
  const rows = (data as Row[] | null) ?? [];
  return ((rows.find((r) => r.kind !== 'audiobook') ?? rows[0])?.id as string | undefined) ?? null;
}

/**
 * Find one… (Kristi, Oct 30): a summary or cover from a search result you picked, saved the
 * blanks-only way with its source, as a one-book run (review_apply_fills), so Undo is
 * review_undo_fills. The rest of the book's lookup record stays as it was.
 */
export async function fillOne(
  bookId: string,
  row: LookupRow,
  fill: { table: 'works' | 'editions'; row_id: string; field: 'summary' | 'cover_url'; value: string; source: 'Open Library' | 'Google Books' },
): Promise<{ run: string; filled: number }> {
  const res = await rpc<{ run: string; fields_filled: number }>('review_apply_fills', {
    book: bookId,
    fills: [{ ...fill, ...(fill.field === 'summary' ? { with: { summary_source: fill.source } } : {}) }],
    outcome: 'filled',
    needs: row.needs.filter((n) => n !== fill.field),
    problems: [],
    ol_calls: 0,
    gb_calls: 0,
    cands: row.candidates,
    diffs: row.differences,
  });
  booksChanged();
  return { run: res.run, filled: res.fields_filled ?? 0 };
}
